// Coarse "how long ago" used by the POS V2 order view (order age in the
// info header, payment rows in the money pane). One unit only, floored,
// largest unit first — "Hace 2 h", never "Hace 2 h 15 min".

export type TRelativeAgeUnit = 'days' | 'hours' | 'minutes' | 'now';

export interface IRelativeAge {
  unit: TRelativeAgeUnit;
  count: number;
}

export type TRelativeAgeTranslate = (
  key: string,
  params?: Record<string, unknown>
) => string;

const AGE_KEYS: Record<Exclude<TRelativeAgeUnit, 'now'>, string> = {
  days: 'posV2.agoDays',
  hours: 'posV2.agoHours',
  minutes: 'posV2.agoMinutes',
};

/**
 * Null when `from` is not a valid date. Future dates clamp to "now" (device
 * clocks drift; a negative age must never render).
 */
export function getRelativeAge(
  from: Date | string | number,
  now: Date
): IRelativeAge | null {
  const fromMs = new Date(from).getTime();
  if (!Number.isFinite(fromMs)) return null;

  const diff = Math.max(0, now.getTime() - fromMs);
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return { unit: 'days', count: days };
  if (hours > 0) return { unit: 'hours', count: hours };
  if (minutes > 0) return { unit: 'minutes', count: minutes };
  return { unit: 'now', count: 0 };
}

/** Empty string when `from` is not a valid date. */
export function formatRelativeAge(
  from: Date | string | number,
  now: Date,
  t: TRelativeAgeTranslate
): string {
  const age = getRelativeAge(from, now);
  if (!age) return '';
  if (age.unit === 'now') return t('posV2.justNow');
  return t(AGE_KEYS[age.unit], { count: age.count });
}
